import React, { useEffect, useState } from "react";
import { 
  TextField, 
  InputAdornment, 
  IconButton,
  Box,
  Typography,
  styled,
  alpha
} from "@mui/material";
import { Clear as ClearIcon, Search as SearchIcon } from "@mui/icons-material";
import { COLORS } from "../../Styles/Colors";

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    fontSize: '0.75rem',
    backgroundColor: 'white',
    borderRadius: 6,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: alpha(COLORS.teal, 0.04),
    },
    '&.Mui-focused': {
      backgroundColor: 'white',
      boxShadow: `0 0 0 2px ${alpha(COLORS.teal, 0.1)}`,
    },
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#e5e7eb',
      borderWidth: '1px',
      transition: 'border-color 0.2s ease-in-out',
    },
    '&:hover fieldset': {
      borderColor: alpha(COLORS.teal, 0.5),
    },
    '&.Mui-focused fieldset': {
      borderColor: COLORS.teal,
      borderWidth: '1px',
    },
  },
  '& .MuiInputBase-input': {
    padding: '4px 8px',
    height: '24px',
    '&::placeholder': {
      color: '#9ca3af',
      opacity: 1,
    },
  },
  '& .MuiInputAdornment-root': {
    marginLeft: 0,
    marginRight: 0,
  },
}));

const HeaderContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  width: '100%',
  minHeight: '64px',
  paddingTop: '12px',
});

const HeaderTitle = styled(Typography)({
  // Match `StandardHeader` styling from `Components/Table/DataTable.tsx`
  fontSize: '0.7rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: '#64748b',
  letterSpacing: '0.06em',
  lineHeight: 1,
  marginBottom: '6px',
});

export const SearchableHeader = ({
  field,
  headerName,
  searchValue,
  onSearchChange,
  onClearSearch,
}: {
  field: string;
  headerName: string;
  searchValue: string;
  onSearchChange: (field: string, value: string) => void;
  onClearSearch: (field: string) => void;
}) => {
  const [localValue, setLocalValue] = useState(searchValue);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (localValue !== searchValue) {
        onSearchChange(field, localValue);
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [localValue, field, searchValue, onSearchChange]);

  useEffect(() => {
    setLocalValue(searchValue);
  }, [searchValue]);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLocalValue("");
    onClearSearch(field);
  };

  return (
    <HeaderContainer>
      <HeaderTitle>{headerName}</HeaderTitle>
      <StyledTextField
        size="small"
        fullWidth
        placeholder={`Search...`}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon 
                sx={{ 
                  fontSize: 16, 
                  color: isFocused ? COLORS.teal : '#9ca3af',
                  transition: 'color 0.2s ease-in-out'
                }} 
              />
            </InputAdornment>
          ),
          endAdornment: localValue && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={handleClear}
                sx={{
                  padding: '2px',
                  '&:hover': {
                    backgroundColor: alpha(COLORS.teal, 0.1),
                  },
                }}
              >
                <ClearIcon sx={{ fontSize: 14, color: '#6b7280' }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </HeaderContainer>
  );
};